import React, { FC } from 'react'
import styled, { css } from 'styled-components'
import {
  Box,
  Grid,
  IIcon,
  Icon,
  IconButton,
  Text,
  Flex,
  IFlex,
} from '@ads/front-ds'
import { TextLink } from '@ads/front-core/atoms/Link'
import HorizontalLine from '../HorizontalLine'
import { ErrorBoxSize, IErrors } from './interfaces'
import { getColor } from './utils'

const Errors: FC<IErrors & IFlex> = ({
  errors,
  footer,
  boxSize,
  className,
  ...restFlex
}) => {
  const getItemsInColumn = (itemsInColumn: number, itemsLength: number) =>
    Math.min(itemsInColumn, itemsLength)

  return (
    <Flex gap="xxs" flexDirection="column" {...restFlex}>
      {errors.map((error, i) => (
        <VariantWrapper
          key={`error-${error.variant}-${i}`}
          flexDirection="column"
          gap="s"
          colorLeftBorder={getColor(error.variant)}
          withLabel={!!error.label}
          withLabelIcon={!!error.iconName}
          boxSize={boxSize}
          className={className}
        >
          {error.iconName || error.label || error.onClose ? (
            <Flex gap="xxs" justifyContent="space-between">
              <Flex gap="xxs" alignItems="flex-start">
                {error.iconName ? (
                  <Box>
                    <LabelIcon
                      name={error.iconName}
                      color={getColor(error.variant)}
                    />
                  </Box>
                ) : null}
                {!!error.label && (
                  <Text fontWeight="medium">{error.label}</Text>
                )}
              </Flex>
              {error.onClose && (
                <IconButton
                  name="20-close"
                  variant="bareDark"
                  onClick={error.onClose}
                />
              )}
            </Flex>
          ) : null}
          <ItemsWrapper
            gap="s"
            columns={getItemsInColumn(error.itemsInColumn, error.items.length)}
            withLabelIcon={!!error.iconName}
          >
            {error.items.map((item, i) => (
              <ItemWrapper
                key={`errorInfo-${i}`}
                columns={item.Icon ? 2 : 1}
                gap="xs"
                withSeparator={error.withSeparator}
                withIcon={item.Icon}
                itemsCount={error.items.length}
                itemsInColumn={getItemsInColumn(
                  error.itemsInColumn,
                  error.items.length,
                )}
                boxSize={boxSize}
              >
                {item.Icon}
                <Box>
                  <Text
                    lineHeight={boxSize === ErrorBoxSize.SMALL ? 'xs' : 'm'}
                    fontSize={boxSize === ErrorBoxSize.SMALL ? 's' : 'm'}
                  >
                    {item.Message}
                  </Text>
                  {item.url && (
                    <TextLinkStyled to={item.url}>
                      {item.urlLabel}
                    </TextLinkStyled>
                  )}
                </Box>
              </ItemWrapper>
            ))}
          </ItemsWrapper>

          {footer && (
            <>
              <HorizontalLine mt="m" mb="xxs" />
              <Box mb="xxs">{footer}</Box>
            </>
          )}
        </VariantWrapper>
      ))}
    </Flex>
  )
}

const VariantWrapper = styled(Flex)<{
  colorLeftBorder: IIcon['color']
  withLabel?: boolean
  withLabelIcon?: boolean
  boxSize?: ErrorBoxSize
  className?: string
}>`
  ${p => css`
    ${p.colorLeftBorder &&
    css`
      border: ${p.theme.borders.thinGrey20};
      border-left: 5px solid ${p.theme.colors[p.colorLeftBorder]};
    `}
    border-radius: ${p.theme.space.xxxs}px;
    padding: ${p.withLabel ? 19 : p.theme.space.xs}px 24px
      ${p.theme.space.xxs}px
      ${p.withLabelIcon ? p.theme.space.xxs : p.theme.space.m}px;

    ${p.boxSize === ErrorBoxSize.SMALL &&
    css`
      padding: 4px 10px;
    `}
    ${p.boxSize === ErrorBoxSize.MEDIUM &&
    css`
      padding: ${p.theme.space.xxs}px 10px;
    `}
  `}
`

const LabelIcon = styled(Icon)`
  ${p => css`
    width: ${p.theme.space.s}px;
    height: ${p.theme.space.s}px;
  `}
`

const ItemsWrapper = styled(Grid)<{ withLabelIcon?: boolean }>`
  ${p => css`
    padding-left: ${p.withLabelIcon ? p.theme.space.m : 0}px;
  `}
`

const ItemWrapper = styled(Grid)<{
  withIcon: React.ReactElement | IIcon['name']
  withSeparator: boolean
  itemsCount: number
  itemsInColumn: number
  boxSize?: ErrorBoxSize
}>`
  ${p => css`
    position: relative;
    flex-grow: 1;
    padding: 0 ${p.theme.space.s}px 0 0;
    align-items: start;
    grid-template-columns: ${p.withIcon ? 'min-content auto' : 'auto'};

    ${p.boxSize === ErrorBoxSize.DEFAULT &&
    css`
      &:last-child {
        padding-bottom: ${p.theme.space.s}px;
      }
    `}

    ${p.withSeparator &&
    css`
      &:after {
        content: '';
        position: absolute;
        height: 1px;
        background-color: ${p.theme.colors.grey20};
        bottom: -1px;
        right: ${p.theme.space.s}px;
        left: 0px;
      }

      &:not(:nth-child(${p.itemsInColumn}n)) {
        border-right: ${p.theme.borders.thinGrey20};
      }

      &:last-child {
        border-right-style: none;
        margin-right: 0px;
      }

      &:nth-last-child(${p.itemsInColumn > 1
            ? `-n + ${p.itemsInColumn}`
            : '1'}) {
        &:after {
          content: none;
        }
      }
    `}

    ${p.itemsCount === p.itemsInColumn &&
    css`
        padding-top: 0;
        padding-bottom: 0;
      }
    `}
  `}
`

const TextLinkStyled = styled(TextLink)`
  ${p => css`
    margin-top: 6px;
    display: block;
    font-size: ${p.theme.fontSizes.m}px;
    font-weight: ${p.theme.fontWeights.bold};
  `}
`

export default Errors
